import { useFormContext } from 'react-hook-form';
import { Repeat, Pencil, X } from 'lucide-react';
import type { AppointmentFormValues } from '@/types/appointment-form';

interface RecurrenceSectionProps {
  /** Opens the full-screen Recurrence configurator (Flutter
   * `recurrence_appointment_widget.dart`). */
  onEdit?: () => void;
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmtDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${MONTH_SHORT[d.getMonth()]} ${String(d.getDate()).padStart(2, '0')}, ${d.getFullYear()}`;
}

/**
 * RecurrenceSection — selected-recurrence summary card. Mirrors Flutter
 * `recurrence_widget.dart`: shows "Recurrence • occurrence X of Y" with
 * the start/end date span, an edit pencil that opens the configurator,
 * and a × that clears the recurrence. Hidden when `recurring === null`.
 */
export function RecurrenceSection({ onEdit }: RecurrenceSectionProps = {}) {
  const { watch, setValue } = useFormContext<AppointmentFormValues>();
  const recurring = watch('recurring');

  if (!recurring) return null;

  const { startDate, endDate, repeatEvery, numberOfRepeat, period, days } = recurring;
  const periodSingular = (period ?? 'Days').replace(/s$/, '').toLowerCase();
  const summary = `Every ${repeatEvery} ${periodSingular}${repeatEvery === 1 ? '' : 's'} · ${numberOfRepeat} occurrence${numberOfRepeat === 1 ? '' : 's'}`;
  const dayLine =
    period === 'Weeks' && days?.length
      ? days.map((d) => d.slice(0, 3)).join(' · ')
      : null;

  return (
    <div className="px-4 py-3 border-t border-border-subtle">
      <p className="text-xs font-medium text-text-muted mb-2">
        Recurrence
      </p>
      <div className="flex items-start gap-3 p-3 rounded-lg border border-border-default bg-surface-card">
        <Repeat className="h-5 w-5 text-brand-primary flex-shrink-0 mt-1" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-text-primary leading-snug">
            {summary}
          </p>
          <p className="text-xs text-text-muted leading-snug">
            {fmtDate(startDate)} — {fmtDate(endDate)}
          </p>
          {dayLine ? (
            <p className="text-xs text-text-muted leading-snug">{dayLine}</p>
          ) : null}
        </div>
        {onEdit ? (
          <button
            type="button"
            onClick={onEdit}
            className="p-1 hover:bg-grey-100 rounded flex-shrink-0"
            aria-label="Edit recurrence"
          >
            <Pencil className="h-4 w-4 text-brand-primary" />
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => setValue('recurring', null, { shouldValidate: true })}
          className="p-1 hover:bg-grey-100 rounded flex-shrink-0"
          aria-label="Clear recurrence"
        >
          <X className="h-4 w-4 text-status-error" />
        </button>
      </div>
    </div>
  );
}
